///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isEqual } from "lodash";

import OutlinedInput from "../../form-controls/OutlinedInput";
import { statusOptions, patterns, status } from "../../../helpers/types";
import messages from "../../../helpers/messages";
import AutoComplete from "../../form-controls/AutoComplete";
import {
  initialState,
  setLaneMatchFilter,
  fetchMatchingLanes,
  saveEditedLane,
  saveLaneEditsReset,
  resetLaneMatchGridFilters,
  toggleConfirmCancellationModal
} from "../../../slices/laneMatchSlice";
import Spinner from "../../icons/Spinner";
import Alert from "../../Alert";
import ConfirmCancellationModal from "../details/ConfirmCancellationModal";
import IconButton from "../../form-controls/IconButton";
import ClearFilterIcon from "../../icons/ClearFilterIcon";
import getCurrency from "../../../helpers/getCurrency";
import getPastMonthsOption from "../../../helpers/getPastMonthsOption";
import { getLaneMatchCountForTLLanes } from "../../../slices/bidPricingSlice";

const bidStatusOptions = [
  ...statusOptions.filter(({ value }) =>
    [status.inProgress, status.submitted, status.feedback].find(
      statusId => statusId === value
    )
  ),
  { value: status.closed, label: messages.closed.defaultMessage }
];

export const LaneMatchFilters = ({ role, loading, unsavedChanges }) => {
  const dispatch = useDispatch();
  const {
    laneMatch: {
      filters: laneMatchFilters,
      matchingLaneRequestLaneId,
      saveLaneEditsLoading,
      saveLaneEditsFailure,
      cancellationDetails,
      filtersApplied,
      division: bidDivision
    },
    bidPricing
  } = useSelector(state => state);
  const [filters, setFilters] = useState(initialState.filters);
  const [showVolumeValidation, toggleVolumeValidation] = useState(true);

  const { minVolume, maxVolume, minCost, monthRange, statuses } = filters;

  useEffect(() => setFilters({ ...laneMatchFilters }), [laneMatchFilters]);

  const handleChange = (value, field, pattern, numeric = false) => {
    if (!pattern || value.match(pattern)) {
      const convertedValue = numeric && value === "" ? null : parseFloat(value);
      setFilters({
        ...filters,
        [field]: numeric ? convertedValue : value
      });
    }
  };
  const truckloadLane = bidPricing.lanes?.find(
    x => x.truckloadLaneId === matchingLaneRequestLaneId
  );

  const invalidVol =
    minVolume !== null && maxVolume !== null && minVolume > maxVolume;

  const handleResetFilters = () => dispatch(resetLaneMatchGridFilters());

  const handleApply = () => {
    dispatch(setLaneMatchFilter(filters));
    dispatch(getLaneMatchCountForTLLanes());
    dispatch(
      fetchMatchingLanes({
        role,
        truckloadLane,
        resetLaneMatchFilters: false
      })
    );
  };

  const disableApplyButton =
    invalidVol ||
    !statuses ||
    !statuses.length ||
    isEqual(filters, laneMatchFilters);

  const handleCancelDialogExit = () =>
    dispatch(
      fetchMatchingLanes({
        role,
        truckloadLane: bidPricing.lanes.find(
          l =>
            l.truckloadLaneId ===
            (cancellationDetails.showModal
              ? cancellationDetails.newTruckloadLaneId
              : matchingLaneRequestLaneId)
        ),
        clearFilterAndSortDetails: false,
        resetLaneMatchFilters: false,
        discardUnsavedChanges: true
      })
    );

  const mappedStatusOptions = bidStatusOptions.filter(({ value }) =>
    statuses?.find(s => s === value)
  );

  const last3Months = getPastMonthsOption(3);

  return (
    <div className="filter-row px-12">
      <Alert
        handleClose={() => dispatch(saveLaneEditsReset())}
        title={messages.systemError.defaultMessage}
        message={messages.saveLanesErrorMessage.defaultMessage}
        open={saveLaneEditsFailure}
        modalStyle={{ minHeight: 190 }}
      />
      <ConfirmCancellationModal
        open={cancellationDetails.showModal}
        closeModal={() =>
          dispatch(toggleConfirmCancellationModal({ showModal: false }))
        }
        handleExit={handleCancelDialogExit}
      />

      <div className="grid__cell">
        <div className="grid__cell">
          <OutlinedInput
            data-testid="min-vol"
            value={minVolume ?? ""}
            onChange={e =>
              handleChange(e.target.value, "minVolume", patterns.intSix, true)
            }
            label={messages.minVol.defaultMessage}
            onFocus={() => toggleVolumeValidation(false)}
            onBlur={() => toggleVolumeValidation(true)}
            error={invalidVol && showVolumeValidation}
          />
        </div>
        <div className="grid__cell pl-8">
          <OutlinedInput
            data-testid="max-vol"
            value={maxVolume ?? ""}
            onChange={e =>
              handleChange(e.target.value, "maxVolume", patterns.intSix, true)
            }
            label={messages.maxVol.defaultMessage}
            onFocus={() => toggleVolumeValidation(false)}
            onBlur={() => toggleVolumeValidation(true)}
            error={invalidVol && showVolumeValidation}
          />
        </div>
      </div>

      <div className="grid__cell pl-24">
        <div className="grid__cell">
          <OutlinedInput
            data-testid="min-cost"
            value={minCost ?? ""}
            onChange={e =>
              handleChange(
                e.target.value,
                "minCost",
                patterns.intSixFractionalTwo,
                true
              )
            }
            label={messages.minCost.defaultMessage}
            unit={getCurrency(bidDivision)}
            isUnitPositionLeft={true}
          />
        </div>
        <div className="grid__cell pl-8" data-testid="month-range">
          <AutoComplete
            value={last3Months.find(o => o.value === monthRange)}
            staticOptions={last3Months}
            onChange={monthRange =>
              handleChange(monthRange.value, "monthRange")
            }
            label={messages.startingMonth.defaultMessage}
            backspaceRemovesValue={false}
          />
        </div>
      </div>
      <div className="grid__cell pl-8 bid-status">
        <AutoComplete
          value={mappedStatusOptions?.length > 0 ? mappedStatusOptions : null}
          staticOptions={bidStatusOptions}
          handleChange={options =>
            handleChange(
              options?.map(({ value }) => value),
              "statuses"
            )
          }
          label={messages.status.defaultMessage}
          isMulti={true}
          error={(!statuses || statuses.length === 0) && !loading}
        />
      </div>

      {unsavedChanges && (
        <>
          <button
            style={{ minWidth: 80 }}
            className={"h-32 text-button font-size-14 fr font-bold mr-16"}
            data-testid="cancel-save-lanes"
            onClick={() =>
              dispatch(
                toggleConfirmCancellationModal({
                  showModal: true,
                  newTruckloadLaneId: matchingLaneRequestLaneId
                })
              )
            }
          >
            {messages.cancel.defaultMessage}
          </button>
          <button
            data-testid="save-button"
            disabled={invalidVol}
            className="h-32 secondary-button univers-67-bold-cond fr mr-16 save"
            onClick={() => dispatch(saveEditedLane(role))}
          >
            {saveLaneEditsLoading ? (
              <Spinner
                fill="#69C764"
                className="spinner"
                height="16px"
                width="16px"
              />
            ) : (
              messages.saveChanges.defaultMessage
            )}
          </button>
        </>
      )}

      <button
        data-testid="apply-filter-button"
        disabled={disableApplyButton}
        className="h-32 text-button univers-67-bold-cond short-button lane-match ml-16 mt-2"
        onClick={handleApply}
      >
        {messages.apply.defaultMessage}
      </button>
      <div className="fr mr-36">
        <IconButton
          title={messages.clearFilters.defaultMessage}
          disabled={!filtersApplied}
          dataTestId="clear-filter-button"
          onClick={handleResetFilters}
          icon={ClearFilterIcon}
          offset={[7, 6]}
        />
      </div>
    </div>
  );
};

export default React.memo(LaneMatchFilters);
